// Per-page renderer for "commonality" — extracted from reportBuilder.js.
// Signature: async (api, doc, ctx) => { y, aiInsights, aiRecommendations }
import autoTable from 'jspdf-autotable';
import { NAVY, fetchPageAI } from '../utils';
import { drawBarChart } from '../charts';

export async function renderCommonality(api, doc, ctx) {
  const { qs, filterDesc, cs, pw, page, filters } = ctx;
  let y = ctx.y;
  let aiInsights = [];
  let aiRecommendations = [];

  const res = await api.get(`/analytics/commonality?${qs}`);
  const d = res.data;

  const supLines = (d.common_suppliers || []).slice(0, 10).map(s => `- ${s.supplier}: ${s.department_count} locations, ${cs}${s.total_spend?.toLocaleString()}, consolidation=${s.consolidation_opportunity}`).join('\n');
  const itemLines = (d.item_commonality || []).slice(0, 10).map(i => `- ${i.item} [${i.level}]: ${i.supplier_count} suppliers, ${cs}${(i.total_spend || 0).toLocaleString()}`).join('\n');
  const stdLines = (d.standardization_opportunities || []).slice(0, 10).map(o => `- ${o.category}: ${o.supplier_count} suppliers — ${o.opportunity}`).join('\n');
  const dataContext = `Common Suppliers: ${d.common_suppliers?.length}\nTotal Locations: ${d.total_departments}\nConsolidation Opportunities: ${d.common_suppliers?.filter(s => s.consolidation_opportunity).length}\nMulti-supplier Items: ${d.item_commonality?.length || 0}\nStandardization Opportunities: ${d.standardization_opportunities?.length || 0}\n\nTop Common Suppliers:\n${supLines}\n\nTop Multi-Supplier Items:\n${itemLines}\n\nStandardization:\n${stdLines}`;

  try {
    const aiRes = await fetchPageAI(api, { page, dataContext, filterDesc, filters });
    aiInsights = aiRes.insights;
    aiRecommendations = aiRes.recommendations;
  } catch {}

  autoTable(doc, {
    startY: y,
    head: [['Common Suppliers', 'Total Locations', 'Consolidation Opps']],
    body: [[String(d.common_suppliers?.length), String(d.total_departments), String(d.common_suppliers?.filter(s => s.consolidation_opportunity).length)]],
    styles: { fontSize: 9, halign: 'center' },
    headStyles: { fillColor: NAVY },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  if (d.common_suppliers?.length > 0) {
    const chartData = d.common_suppliers.slice(0, 10).map(s => ({ label: s.supplier, value: s.total_spend }));
    y = drawBarChart(doc, 14, y, pw - 28, 70, chartData, { title: 'Common Suppliers by Spend' });

    autoTable(doc, {
      startY: y,
      head: [['Supplier', 'Locations', 'Spend', 'High Priority']],
      body: d.common_suppliers.slice(0, 15).map(s => [s.supplier, String(s.department_count), `${cs}${s.total_spend?.toLocaleString()}`, s.consolidation_opportunity ? 'Yes' : 'No']),
      styles: { fontSize: 7 }, headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Item Commonality — items sourced from multiple suppliers (negotiation lever).
  if (d.item_commonality?.length > 0) {
    if (y > 200) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Item', 'Level', 'Suppliers', 'Total Spend', 'Transactions']],
      body: d.item_commonality.slice(0, 20).map(i => [
        i.item || 'Unknown',
        i.level || '',
        String(i.supplier_count || 0),
        `${cs}${(i.total_spend || 0).toLocaleString()}`,
        String(i.transaction_count || 0),
      ]),
      styles: { fontSize: 7 }, headStyles: { fillColor: NAVY },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Standardization Opportunities — categories with multiple suppliers (executive action list).
  if (d.standardization_opportunities?.length > 0) {
    if (y > 210) { doc.addPage(); y = 15; }
    autoTable(doc, {
      startY: y,
      head: [['Category', 'Suppliers', 'Opportunity']],
      body: d.standardization_opportunities.slice(0, 15).map(o => [
        o.category || 'Unknown',
        String(o.supplier_count || 0),
        o.opportunity || '',
      ]),
      styles: { fontSize: 7 }, headStyles: { fillColor: NAVY },
      columnStyles: { 2: { cellWidth: 'auto' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  return { y, aiInsights, aiRecommendations };
}
